import React from "react";
import { graphql } from "gatsby";

import Layout from "../../components/Layout";
import Faq, { convertSupportCentreToFaqs } from "../../components/Faq";

import {
  Hero,
  WhatIsZipStocks,
  WhatStocksCanBuy,
  HowToBuyStocks,
  WhyUseZipmex,
  HereToHelp,
} from "../../components/partials/zipstocks";

import WithI18next from "../../components/WithI18next";
import GetOnBoard from "../../components/ContentComponents/GetOnBoard";

import { getAcfPartialContent } from "../../utils";

const ZipStock = ({ data, t }) => {
  const getOnBoardContent = getAcfPartialContent(data.investAnywhereContent);

  return (
    <Layout
      title={t("zipstocks.seo.title")}
      description={t("zipstocks.seo.description")}
    >
      <Hero classNames="zipstocks-page" content={data.heroContent} />

      <WhatIsZipStocks content={data.whatIsZipStocksContent} />

      <WhatStocksCanBuy stocks={data.stocks} />

      <HowToBuyStocks
        content={data.howToBuyContent}
        stepContents={data.howToBuySteps}
      />

      <WhyUseZipmex
        content={data.whyUseContent}
        reasonContents={data.whyUseReasons}
      />

      <HereToHelp content={data.hereToHelpContent} />

      <Faq
        content={convertSupportCentreToFaqs(data.faqContent)}
        visitSupportUrl={`/support/other-topics/`}
      />

      <GetOnBoard
        content={getOnBoardContent}
        btnClassNames="button-secondary button-rounded"
      />
    </Layout>
  );
};

export default WithI18next()(ZipStock);

export const query = graphql`
  query ($wpLocale: String!, $wpGlobalConfig: String!) {
    heroContent: allWpHeroSectionItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfHeroSections: { hsPage: { eq: "zipstocks" } }
      }
    ) {
      edges {
        node {
          acfHeroSections {
            hsMainHeading
            hsMainText
            hsButtonText
            hsButtonLink
          }
        }
      }
    }

    heroImage: file(relativePath: { eq: "images/hero-zipstocks.png" }) {
      childImageSharp {
        gatsbyImageData(width: 350, placeholder: BLURRED)
      }
    }

    whatIsZipStocksContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "zipstocks-what-is-zipstocks" } }
      }
    ) {
      nodes {
        acfPartials {
          pContent
          pButtonText
          pButtonUrl
        }
      }
    }

    stocks: allWpStockItem(
      filter: { acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } } }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfStockItem {
            fullName
            shortName
            link
            image {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    }

    howToBuyContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "zipstocks-how-to-buy" } }
      }
    ) {
      nodes {
        acfPartials {
          pHeading
          pContent
        }
      }
    }

    howToBuySteps: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "zipstocks-how-to-buy-steps" } }
      }
      sort: { order: ASC, fields: date }
    ) {
      nodes {
        acfPartials {
          pHeading
          pContent
          pIcon {
            localFile {
              publicURL
            }
          }
        }
      }
    }

    whyUseContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "zipstocks-why-use-zipmex" } }
      }
    ) {
      nodes {
        acfPartials {
          pHeading
          pContent
          pButtonText
          pButtonUrl
        }
      }
    }

    whyUseReasons: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "zipstocks-why-use-zipmex-reason" } }
      }
      sort: { order: ASC, fields: date }
    ) {
      nodes {
        acfPartials {
          pHeading
          pContent
          pElementId
        }
      }
    }

    hereToHelpContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "zipstocks-here-to-help" } }
      }
      sort: { order: ASC, fields: date }
    ) {
      nodes {
        acfPartials {
          pHeading
          pContent
          pButtonText
          pButtonUrl
        }
      }
    }

    faqContent: allWpSupportCenterItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { in: [".all", $wpGlobalConfig] } }
        categories: { nodes: { elemMatch: { slug: { eq: "zipstocks" } } } }
      }
      sort: { order: DESC, fields: date }
    ) {
      edges {
        node {
          title
          content
        }
      }
    }

    investAnywhereContent: allWpPartialItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "zipstocks-invest-anywhere-anytime" } }
      }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pContent
            pButtonText
            pButtonUrl
          }
        }
      }
    }
  }
`;
